import React, { useState, useEffect } from 'react';
import { Modal, Button, Tabs, Tab, Alert, Spinner, Badge } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { FaCheckCircle, FaInfoCircle, FaDownload } from 'react-icons/fa';
import DOMPurify from 'dompurify';
import './TermsModal.css';
import { getAcceptanceStatus, acceptTerms } from 'Services/TermsService';
import { getMediaUrl } from 'Utils/Helpers';

const TermsAcceptanceModal = ({ onAccepted }) => {
    const [terms, setTerms] = useState(null);
    const [accepting, setAccepting] = useState(false);
    const [activeTab, setActiveTab] = useState(0);
    const [fileDownloaded, setFileDownloaded] = useState(false);

    useEffect(() => {
        checkAcceptanceStatus();
    }, []);

    const checkAcceptanceStatus = async () => {
        try {
            const response = await getAcceptanceStatus();
            if (!response.data.hasAcceptedLatest && response.data.latestTerms) {
                setTerms(response.data.latestTerms);
            } else {
                // L’utilisateur a déjà accepté la dernière version
                onAccepted();
            }
        } catch (err) {
            console.error('Échec de la vérification du statut d’acceptation', err);
            toast.error('Échec du chargement des conditions générales');
        } finally {
            console.log('Chargement des conditions terminé.');
        }
    };

    const handleAccept = async () => {
        setAccepting(true);

        try {
            await acceptTerms({
                termsAndConditionsId: terms.id,
            });
            toast.success('Conditions générales acceptées avec succès');
            onAccepted();
        } catch (err) {
            toast.error(err.response?.data?.message || 'Échec de l’acceptation des conditions');
            setAccepting(false);
        }
    };

    const sanitizeHTML = (html) => {
        return { __html: DOMPurify.sanitize(html) };
    };

    if (!terms) {
        return null;
    }

    return (
        <Modal show={true} backdrop="static" keyboard={false} centered size="lg" className="terms-acceptance-modal">
            <Modal.Header closeButton={false} className="bg-primary text-white">
                <Modal.Title className="w-100">
                    <div className="d-flex justify-content-between align-items-center">
                        <span>Conditions Générales d’Utilisation</span>
                        <Badge bg="light" text="dark" pill>
                            Version {terms.version}
                        </Badge>
                    </div>
                    <small className="d-block mt-2" style={{ fontSize: '0.85rem', opacity: 0.9 }}>
                        Veuillez lire et accepter ces conditions pour continuer à utiliser la plateforme.
                    </small>
                </Modal.Title>
            </Modal.Header>

            <Alert variant="warning" className="mb-0 rounded-0">
                <strong>Important :</strong> Vous devez lire et accepter ces conditions pour continuer à utiliser le
                service.
            </Alert>

            {terms.changeDescription && (
                <Alert variant="info" className="mb-0 rounded-0 d-flex align-items-start">
                    <FaInfoCircle className="me-2 mt-1" />
                    &nbsp;
                    <div>
                        <strong>Ce qui a changé :</strong>
                        <div className="mt-1">{terms.changeDescription}</div>
                    </div>
                </Alert>
            )}

            <Modal.Body className="p-0">
                {terms.tabs && terms.tabs.length > 1 ? (
                    <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(parseInt(k))} className="px-3 pt-3">
                        {terms.tabs.map((tab, index) => (
                            <Tab eventKey={index} title={tab.title} key={index}>
                                <div className="p-4 modal-content-area">
                                    <div
                                        className="terms-html-content"
                                        dangerouslySetInnerHTML={sanitizeHTML(tab.content)}
                                    />
                                </div>
                            </Tab>
                        ))}
                    </Tabs>
                ) : (
                    <div className="p-4 modal-content-area">
                        {terms.tabs && terms.tabs[0] && (
                            <>
                                <h3>{terms.tabs[0].title}</h3>
                                <div
                                    className="terms-html-content"
                                    dangerouslySetInnerHTML={sanitizeHTML(terms.tabs[0].content)}
                                />
                            </>
                        )}
                    </div>
                )}
            </Modal.Body>

            <Modal.Footer className="flex-column gap-2 align-items-stretch">
                {terms.fileUrl && (
                    <div className="d-flex align-items-center gap-3 w-100">
                        <a
                            href={getMediaUrl(terms.fileUrl)}
                            download
                            target="_blank"
                            rel="noreferrer"
                            className="btn btn-outline-secondary"
                            onClick={() => setFileDownloaded(true)}
                        >
                            <FaDownload className="me-2" />
                            Télécharger le document
                        </a>
                        {!fileDownloaded && (
                            <small className="text-danger">Vous devez télécharger le document avant d’accepter.</small>
                        )}
                    </div>
                )}
                <div className="d-flex justify-content-between align-items-center w-100">
                    <small className="text-muted">
                        En cliquant sur « J’accepte », vous confirmez votre accord avec ces conditions. Votre
                        acceptation sera enregistrée.
                    </small>
                    <Button
                        variant="primary"
                        size="lg"
                        onClick={handleAccept}
                        disabled={accepting || (terms.fileUrl && !fileDownloaded)}
                    >
                        {accepting ? (
                            <>
                                <Spinner animation="border" size="sm" className="me-2" />
                                &nbsp; Traitement en cours...
                            </>
                        ) : (
                            <>
                                <FaCheckCircle className="me-2" /> &nbsp; J’accepte
                            </>
                        )}
                    </Button>
                </div>
            </Modal.Footer>
        </Modal>
    );
};

export default TermsAcceptanceModal;
